import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getHeadlines } from "./api";
import type { GetHeadlinesParams } from "./types";
import useAuth from "@/hooks/useAuth";
import { useMemo, useEffect, useState, useRef } from "react";
import type {
  Headline,
  HeadlineTimestamp,
} from "@/widget-components/newsquawk-headlines/models/Headline";
import headlinesSSEClient from "./headlinesSSEClient";
import {
  shouldHideHeadline,
  filterVisibleHeadlines,
} from "./headlineVisibility";
import { playNotificationSound } from "@/utils/sound-utils";
import {
  getNotificationPermission,
  showBrowserNotification,
} from "@/utils/browser-notification-utils";
import { alertTitle, matchingAlertLists } from "@/shared/utils/alertLists";
import { useUserPreferences } from "@/pages/settings/hooks/useUserPreferences";
import { useNotificationSoundSettings } from "@/pages/settings/hooks/useNotificationSoundSettings";
import { useBrowserNotificationSettings } from "@/pages/settings/hooks/useBrowserNotificationSettings";
import type { ConnectionStatus } from "@/shared/services/configurable-sse-client";
import {
  useHeadlinesPortfoliosWithSoundEnabled,
  useHeadlinesPortfoliosWithPopupEnabled,
  useHeadlinesCombinedWithSoundEnabled,
  useHeadlinesCombinedWithPopupEnabled,
} from "./useHeadlinesPortfoliosWithSoundEnabled";
import {
  headlineFilterMatcher,
  headlinePortfolioMatcher,
} from "@/widget-components/newsquawk-headlines/utils";
import { firstMatchingView } from "@/shared/utils/savedViewHighlight";

/** Maximum headlines to keep in the first page of the infinite query */
const MAX_FIRST_PAGE = 200;

interface UseHeadlinesOptions extends GetHeadlinesParams {
  subscribe?: boolean;
}

/**
 * Gets the Unix timestamp from a headline's published_at field.
 * Handles both HeadlineTimestamp object and ISO string formats.
 */
const getUnixTimestamp = (publishedAt: HeadlineTimestamp | string): number => {
  if (typeof publishedAt === "string") {
    return Date.parse(publishedAt) / 1000;
  }
  return publishedAt.unix;
};

export const useHeadlines = (options?: UseHeadlinesOptions) => {
  const { subscribe = true, ...params } = options || {};
  const { authenticated } = useAuth();
  const { cursor, ...restParams } = params || {};
  const queryClient = useQueryClient();

  // Use ref to store restParams for stable reference in effect
  const restParamsRef = useRef(restParams);
  restParamsRef.current = restParams;

  const [subscribeStatus, setSubscribeStatus] =
    useState<ConnectionStatus>("inactive");

  // Track previous status to detect reconnection
  const prevSubscribeStatusRef = useRef<ConnectionStatus>("inactive");

  const { notificationsSound, isLoading: isUserPrefLoading } =
    useUserPreferences();

  // Keep a stable reference to portfolios with sound enabled (for watchlist alerts)
  const portfoliosWithSoundEnabledRef = useHeadlinesPortfoliosWithSoundEnabled();
  // And combined watchlists, matched in full — tags included — below.
  const combinedWithSoundEnabledRef = useHeadlinesCombinedWithSoundEnabled();
  const combinedWithPopupEnabledRef = useHeadlinesCombinedWithPopupEnabled();

  // Per-channel sound choice + volume. Held in a ref so the SSE message handler
  // reads current values without re-subscribing when the user tweaks settings.
  const { settings: notificationSoundSettings } = useNotificationSoundSettings();
  const notificationSoundSettingsRef = useRef(notificationSoundSettings);
  notificationSoundSettingsRef.current = notificationSoundSettings;

  // Browser (OS) pop-up notifications — popup-enabled watchlists + global
  // toggles, both read via refs inside the SSE handler.
  const portfoliosWithPopupEnabledRef = useHeadlinesPortfoliosWithPopupEnabled();
  const { settings: browserNotifySettings } = useBrowserNotificationSettings();
  const browserNotifySettingsRef = useRef(browserNotifySettings);
  browserNotifySettingsRef.current = browserNotifySettings;

  const headlinesQuery = useInfiniteQuery({
    queryKey: ["headlines", restParams],
    queryFn: ({ pageParam }) =>
      getHeadlines({
        ...restParams,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      // Return the next cursor if there are more pages
      return lastPage.pagination.has_more
        ? lastPage.pagination.next_cursor
        : undefined;
    },
    initialPageParam: cursor as string | undefined,
    enabled: authenticated,

    // refetch configs
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Flatten all pages into a single array of headlines, sorted chronologically
  const headlines = useMemo(() => {
    if (!headlinesQuery.data?.pages) return [];
    const all = headlinesQuery.data.pages.reduce((acc: Headline[], page) => {
      // Hidden headlines are dropped here rather than in queryFn so the cache
      // keeps mirroring the server response — the SSE handler's dedup and
      // in-place update logic below still needs to find them by id.
      return [...acc, ...filterVisibleHeadlines(page.data)];
    }, []);
    // Defensive sort — ensures consistent chronological order regardless of
    // whether data came from SSE insertion, polling, or a background refetch
    return all.sort((a, b) => {
      const aTime = typeof a.published_at === "object" ? a.published_at.unix * 1000 : new Date(a.published_at).getTime();
      const bTime = typeof b.published_at === "object" ? b.published_at.unix * 1000 : new Date(b.published_at).getTime();
      return bTime - aTime; // newest first
    });
  }, [headlinesQuery.data]);

  // Get the last page's pagination info
  const pagination = useMemo(() => {
    if (!headlinesQuery.data?.pages) return undefined;
    const lastPage =
      headlinesQuery.data.pages[headlinesQuery.data.pages.length - 1];
    return lastPage?.pagination;
  }, [headlinesQuery.data]);

  const hasMore = useMemo(() => {
    return pagination?.has_more ?? false;
  }, [pagination]);

  const loadNextPage = () => {
    if (hasMore && !headlinesQuery.isFetchingNextPage) {
      headlinesQuery.fetchNextPage();
    }
  };

  const canSubscribe =
    subscribe && authenticated && !isUserPrefLoading;
  /**
   * Handle incoming headline messages from SSE
   */
  useEffect(() => {
    if (!canSubscribe) return;

    const messageHandler = (data: Headline) => {
      if (data) {
        try {
          // Shared callback for both live SSE pushes and polled/catch-up
          // items — don't assert the transport here.
          console.log("Received new headline:", data);

          const newHeadline = data as Headline;

          // Filter out hidden headlines using the shouldHideHeadline function
          if (shouldHideHeadline(newHeadline)) {
            return;
          }

          // Attach metadata
          newHeadline.isNew = true;
          newHeadline.received_at = Date.now();

          // Update the query cache — sound is played AFTER dedup check
          queryClient.setQueryData(
            ["headlines", restParamsRef.current],
            (oldData: InfiniteData<{ data: Headline[] }>) => {
              if (!oldData) return oldData;

              // Check if headline already exists in any page
              const headlineExists = oldData.pages.some((page) =>
                page.data.some((h: Headline) => h.id === newHeadline.id)
              );

              if (headlineExists) {
                // Check if content actually changed (not just a re-delivery)
                const existingHeadline = oldData.pages
                  .flatMap((p) => p.data)
                  .find((h: Headline) => h.id === newHeadline.id);

                // A differing updated_at means the server edited the headline;
                // an identical value is an SSE re-delivery. updated_at arrives as
                // an ISO string over SSE/REST (not a HeadlineTimestamp), so parse
                // both defensively. existingHeadline is non-null: the headlineExists
                // guard used the same id predicate over the same pages.
                const parseUpdatedAt = (t: unknown): number => {
                  if (typeof t === "string") return Date.parse(t) || 0;
                  if (t && typeof t === "object" && "unix" in t) {
                    return (t as HeadlineTimestamp).unix * 1000;
                  }
                  return 0;
                };
                const existingUpdatedAt = parseUpdatedAt(existingHeadline!.updated_at);
                const newUpdatedAt = parseUpdatedAt(newHeadline.updated_at);
                const hasChanged =
                  newUpdatedAt > 0 &&
                  existingUpdatedAt > 0 &&
                  newUpdatedAt !== existingUpdatedAt;

                if (!hasChanged) {
                  // Same content — SSE re-delivery, skip silently
                  return oldData;
                }

                // Genuine content update — mark for white update flash
                newHeadline.wasUpdated = true;
                newHeadline.received_at = Date.now();
                console.log("Updating existing headline via SSE:", newHeadline);
                return {
                  ...oldData,
                  pages: oldData.pages.map((page) => ({
                    ...page,
                    data: page.data.map((h: Headline) =>
                      h.id === newHeadline.id ? newHeadline : h
                    ),
                  })),
                };
              }

              // Genuinely new headline — play sounds
              if (notificationsSound.headlines)
                playNotificationSound(
                  notificationSoundSettingsRef.current.headlines
                );
              const portfoliosToMatch = portfoliosWithSoundEnabledRef.current ?? [];
              const matchedPortfolio =
                portfoliosToMatch.length > 0 &&
                headlinePortfolioMatcher(newHeadline, portfoliosToMatch);
              // A combined watchlist is a rule over watchlists, tags and
              // keywords, so it is matched as the rule rather than flattened.
              if (
                matchedPortfolio ||
                firstMatchingView(
                  newHeadline,
                  combinedWithSoundEnabledRef.current ?? [],
                  headlineFilterMatcher
                )
              ) {
                playNotificationSound(
                  notificationSoundSettingsRef.current.portfolio
                );
              }

              // Browser pop-up (independent of sound): a match on any list
              // with pop-ups on names every such list, and wins over the
              // generic "all headlines" pop-up. No widget's order applies —
              // one headline raises one pop-up for the whole page.
              if (
                browserNotifySettingsRef.current.enabled &&
                getNotificationPermission() === "granted"
              ) {
                const listAlert = alertTitle(
                  matchingAlertLists(
                    newHeadline,
                    portfoliosWithPopupEnabledRef.current ?? [],
                    combinedWithPopupEnabledRef.current ?? [],
                    headlinePortfolioMatcher,
                    headlineFilterMatcher
                  )
                );
                if (listAlert) {
                  showBrowserNotification({
                    title: listAlert.title,
                    body: newHeadline.subject,
                    tag: listAlert.tag,
                  });
                } else if (browserNotifySettingsRef.current.headlines) {
                  showBrowserNotification({
                    title: "Newsquawk Headline",
                    body: newHeadline.subject,
                    tag: "headlines",
                  });
                }
              }

              // Insert at correct chronological position in first page
              const newTimestamp = getUnixTimestamp(newHeadline.published_at);
              const firstPage = oldData.pages[0];

              // Find correct position based on published_at (descending order - newest first)
              const insertIndex = firstPage.data.findIndex(
                (h: Headline) => getUnixTimestamp(h.published_at) <= newTimestamp
              );

              // If insertIndex is -1, headline is older than all in first page
              // Insert at end of first page (or could belong in a later page, but we keep it here for simplicity)
              const insertAt =
                insertIndex === -1 ? firstPage.data.length : insertIndex;

              const newFirstPageData = [
                ...firstPage.data.slice(0, insertAt),
                newHeadline,
                ...firstPage.data.slice(insertAt),
              ];

              return {
                ...oldData,
                pages: oldData.pages.map((page, index: number) =>
                  index === 0
                    ? {
                        ...page,
                        data: newFirstPageData.length > MAX_FIRST_PAGE
                          ? newFirstPageData.slice(0, MAX_FIRST_PAGE)
                          : newFirstPageData,
                      }
                    : page
                ),
              };
            }
          );
        } catch (error) {
          console.error("Error processing new headline:", error);
        }
      }
    };

    const sub1 = headlinesSSEClient.subscribeTopic({
      topic: "headline",
      callback: messageHandler,
    });

    const sub2 = headlinesSSEClient.onStatusChange(setSubscribeStatus);

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
    // Note: restParams excluded - using ref to avoid effect re-runs on object reference changes
  }, [
    subscribe,
    queryClient,
    notificationsSound.headlines,
    canSubscribe,
    // Stable ref objects from the alert selectors: listing them changes
    // nothing at runtime, it only tells the lint rule so.
    portfoliosWithSoundEnabledRef,
    portfoliosWithPopupEnabledRef,
    combinedWithSoundEnabledRef,
    combinedWithPopupEnabledRef,
  ]);

  /**
   * Refetch headlines when connection re-establishes after being inactive
   * This ensures we get any headlines that were published during SSE downtime
   */
  useEffect(() => {
    const prevStatus = prevSubscribeStatusRef.current;
    const currentStatus = subscribeStatus;

    // Update the ref for next time
    prevSubscribeStatusRef.current = currentStatus;

    // If we transitioned from inactive/pending to active (reconnection)
    if (
      (prevStatus === "inactive" || prevStatus === "pending") &&
      currentStatus === "active" &&
      canSubscribe
    ) {
      console.log(
        "[useHeadlines] Connection re-established, refetching headlines to catch up..."
      );
      headlinesQuery.refetch();
    }
  }, [subscribeStatus, canSubscribe, headlinesQuery]);

  return {
    headlines,
    pagination,
    hasMore,
    loadNextPage,
    loadingNextPage: headlinesQuery.isFetchingNextPage,
    isLoading: headlinesQuery.isLoading,
    isError: headlinesQuery.isError,
    error: headlinesQuery.error,
    subscribeStatus,
    headlinesQuery,
  };
};
